/**
 * Helpers para espaciar peticiones al backend y evitar que las ráfagas de las
 * cargas progresivas sean interpretadas como un ataque por el rate limiter / WAF.
 */

/** Separación entre el inicio de peticiones paralelas de un mismo bloque. */
export const REQUEST_STAGGER_MS = 200

/** Duración mínima de cada bloque mensual en las cargas progresivas. */
export const MIN_BLOCK_DURATION_MS = 800

/** Meses que cubre cada bloque de las cargas progresivas (menos bloques = menos peticiones). */
export const MONTHS_PER_BLOCK = 2

export const sleep = ms => new Promise(resolve => setTimeout(resolve, ms))

/**
 * Agrupa rangos mensuales consecutivos en bloques de `size` meses.
 * Conserva el formato de fecha original: toma el inicio del primero y el fin del último.
 * @param {Array<{ start: string, end: string, month: number, year: number }>} ranges
 * @param {number} size
 * @returns {Array<{ start: string, end: string, month: number, year: number }>}
 */
export function groupRanges(ranges, size = MONTHS_PER_BLOCK) {
  if (!Array.isArray(ranges) || size <= 1) return ranges || []
  const blocks = []
  for (let i = 0; i < ranges.length; i += size) {
    const chunk = ranges.slice(i, i + size)
    const first = chunk[0]
    const last = chunk[chunk.length - 1]
    blocks.push({ ...first, end: last.end })
  }
  return blocks
}

/**
 * Ejecuta las tareas en paralelo pero iniciándolas de forma escalonada.
 * El tiempo total sigue marcado por la tarea más lenta, por eso conviene
 * colocar primero las más pesadas.
 * @param {Array<() => Promise<any>>} tasks
 * @param {number} gapMs
 * @returns {Promise<any[]>} resultados en el mismo orden que `tasks`
 */
export function runStaggered(tasks, gapMs = REQUEST_STAGGER_MS) {
  return Promise.all(
    tasks.map((task, index) => sleep(index * gapMs).then(() => task()))
  )
}

/**
 * Ejecuta la tarea y, si responde antes de `minMs`, espera el tiempo restante.
 * Solo frena los bloques rápidos; los lentos no pagan ningún costo extra.
 * @param {() => Promise<any>} task
 * @param {number} minMs
 * @returns {Promise<any>} resultado de la tarea
 */
export async function runWithMinDuration(task, minMs = MIN_BLOCK_DURATION_MS) {
  const startedAt = Date.now()
  const result = await task()
  const remaining = minMs - (Date.now() - startedAt)
  if (remaining > 0) await sleep(remaining)
  return result
}
